import React from 'react';
import { Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { ElementsRenderer, isWordDocument, UploadedFileViewer } from '../shared';
import { TEXT_TYPE } from '../../constants';
import { useAuth, useStableMediaUrl } from '../../hooks';
import { getMediaBlob } from '../../utils/mediaSessionCache';
import { downloadUploadedFile, viewUploadedFile } from '../shared/uploadedFileActions';
import AiReasonBanner from './AiReasonBanner';
import { Button } from '../ui/button';

const fileNameFromKey = (fileKey) => {
  if (!fileKey) return 'Uploaded file';
  const segment = decodeURIComponent(String(fileKey).split('/').pop() || '');
  return segment || 'Uploaded file';
};

const FileUploadReview = ({ question, response, answerStatus, answerScoreData }) => {
  const { elements, solution, id: questionId } = question;
  const { uploadedFile } = response;
  const { token } = useAuth();

  const fileKey = uploadedFile?.fileKey || null;
  const fileName = uploadedFile?.originalFileName || fileNameFromKey(fileKey);
  const hasFile = Boolean(fileKey);
  const blob = getMediaBlob({ fileKey, questionId }) || null;
  const { mediaUrl, isLoading } = useStableMediaUrl({
    blob,
    fileKey,
    questionId,
    token
  });
  const canOpenFile = Boolean(mediaUrl);
  const isWordFile = isWordDocument(fileName, uploadedFile?.mimeType);

  const handleDownload = () => downloadUploadedFile(mediaUrl, fileName);

  const handleView = () =>
    viewUploadedFile({
      fileUrl: mediaUrl,
      fileName,
      mimeType: uploadedFile?.mimeType
    });

  return (
    <div className="katex-question w-full space-y-4 md:space-y-6">
      {answerScoreData && (
        <AiReasonBanner
          reason={answerScoreData.aiReason}
          answerStatus={answerStatus}
          score={answerScoreData.score}
          maxScore={answerScoreData.maxScore}
        />
      )}

      <div className="question-container text-base leading-relaxed text-foreground md:text-lg">
        <ElementsRenderer elements={elements} textType={TEXT_TYPE.TIPTAP} />
      </div>

      <div className="space-y-3">
        <h4 className="font-medium text-foreground">Your Answer</h4>
        {hasFile ? (
          <div className="overflow-hidden rounded-lg border border-green-200 bg-green-50">
            <div className="flex flex-wrap items-center gap-3 p-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground" title={fileName}>
                  {fileName}
                </p>
                <p className="text-sm text-green-700">
                  {uploadedFile?.sizeInMB ? `${uploadedFile.sizeInMB} MB · ` : ''}
                  File uploaded
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-green-700" aria-label="Loading file" />
                ) : (
                  <>
                    {isWordFile ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleView}
                        disabled={!canOpenFile}
                        className="border-green-300 bg-white"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownload}
                      disabled={!canOpenFile}
                      className="border-green-300 bg-white"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </>
                )}
              </div>
            </div>
            {canOpenFile && !isWordFile ? (
              <div className="border-t border-green-200 bg-white">
                <UploadedFileViewer
                  fileUrl={mediaUrl}
                  fileName={fileName}
                  mimeType={uploadedFile?.mimeType}
                  className="rounded-none border-0"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">No file uploaded.</p>
        )}
      </div>

      {solution?.elements?.length > 0 && (
        <div className="space-y-3 border-t border-border/60 pt-4">
          <h4 className="font-medium text-foreground">Detailed Solution</h4>
          <div className="text-sm leading-relaxed text-muted-foreground">
            <ElementsRenderer elements={solution.elements} textType={TEXT_TYPE.TIPTAP} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadReview;
